// THIS FILE IS USED IN BOTH NODE AND BROWSER

import { remark } from 'remark';
import remarkHTML from 'remark-html';
import remarkGfm from 'remark-gfm-configurable';
import { defaultSchema } from 'hast-util-sanitize';

// include extensions so Node can share this utility
import externalLinks from './externalLinks.js';
import supersub from './remark-supersub';

const gfmOptions = {
  plugins: {
    autolinkLiteral: false
  },
  singleTilde: false
};

// hast-util-sanitize v5.0.1 (used by remark-html) removed default support for target and rel attributes on anchor tags, so we need to apply a custom schema otherwise they get stripped out
// https://github.com/syntax-tree/hast-util-sanitize/releases/tag/5.0.1
const customSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), 'target', 'rel']
  }
};

export const markdownParser = remark()
  .use(externalLinks)
  .use(supersub)
  .use(remarkGfm, gfmOptions)
  .use(remarkHTML, { sanitize: customSchema });

const memoCache = new Map<string, string>();

type RenderMardownOptions = {
  keepOneParagraph?: boolean;
};

/**
 * Renders markdown to HTML.
 *
 * @param str
 * @param param1
 */
export default function renderMarkdown(
  str: string,
  { keepOneParagraph = false }: RenderMardownOptions = {}
) {
  if (!str) return '';

  // if (str.includes('Harassment of any sort ')) debugger;

  const cacheKey = str + '_' + keepOneParagraph;
  const cached = memoCache.get(cacheKey);
  if (cached) return cached;

  // the markdown renderer will wrap a single line in a <p>, which we don't want unless specified by keepOneParagraph for prose blocks.
  const noParagraphs = !(
    str.includes('\n\n') ||
    str.includes('{r}') ||
    keepOneParagraph
  );

  // remove indentation and decode the linebreaks, replace &nbsp; with space
  str = str
    .trim()
    .replace(/(\n+)/g, '$1')
    .replace(/&nbsp;/g, ' ')
    .replace(/\\+\s*{n}/g, '{fbr}')
    .replace(/{br}/g, '\n\n')
    .replace(/{r}/g, '\n\n')
    .replace(/{n}/g, '\n');

  // render HTML
  let html = markdownParser.processSync(str).value.toString() || '';

  // replace forced linebreak placeholders with <br> tags after rendering to html
  html = html.replace(/\{fbr\}/g, '<br>');

  if (noParagraphs) {
    // strip <p> tags
    html = html.replace(/<p>/g, '');
    html = html.replace(/<\/p>/g, '');
  } else {
    // prevent orphans, using &nbsp; for <p> and <li> if last two words are short, based on fiddling with some cases and seeing what works
    html = html.replace(/ (\S{1,7}) (\S{1,10})<\/(p|li)>/g, ' $1&nbsp;$2</$3>');
  }

  html = html.trim();
  memoCache.set(cacheKey, html);

  return html;
}

// NODE COMPATIBLE WINDOW
if (globalThis) globalThis.renderMarkdown = renderMarkdown;
